import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Progress,
  Steps,
  message,
  Divider,
  Alert,
  Checkbox,
  Collapse,
  InputNumber,
  Row,
  Col,
  FloatButton,
} from 'antd';
import {
  RobotOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  FileTextOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { GenerateRequest, ProgressInfo, ExpressionOption, ResolutionOption, ModelOption, SamplerOption, UpscalerOption, ControlNetModelOption, LoraOption } from '@/services/api/character';
import { characterApi } from '@/services/api/character';
import AiAssistantDrawer from './components/AiAssistantDrawer';

const { TextArea } = Input;
const { Panel } = Collapse;

// 表情图标映射
const EXPRESSION_ICONS: Record<string, string> = {
  happy: '😊',
  angry: '😠',
  sad: '😢',
  surprised: '😲',
  shy: '😊',
  confused: '🤔',
  scared: '😨',
  tsundere: '😒',
  excited: '🤩',
  sleepy: '😴',
  playful: '😜',
  neutral: '😐',
  normal: '😐',
  smile: '🙂',
  laugh: '😆',
  teary: '😭',
  thinking: '🤔',
  thinking_tilt: '🤔',
  idle: '😶',
};

// 风格关键字说明
const STYLE_KEYWORDS = {
  anime: {
    name: '动漫风格',
    keywords: 'anime style, anime, cartoon, illustration, 2d, cel shading',
  },
  realistic: {
    name: '真人摄影',
    keywords: 'photorealistic, realistic, photo from real life, 8k, highly detailed, natural lighting, depth of field',
  },
  cg: {
    name: '3D/CG 风格',
    keywords: '3d, cg, 3d render, octane render, unreal engine, blender',
  },
  painting: {
    name: '油画/绘画',
    keywords: 'oil painting, traditional art, painterly, brush strokes, artistic',
  },
  sketch: {
    name: '素描/草图',
    keywords: 'sketch, pencil drawing, line art, monochrome, concept art',
  },
};

// 中文特征到英文提示词的映射
const CHINESE_TO_ENGLISH: Record<string, string> = {
  // 性别
  '男': '1boy',
  '女': '1girl',
  '男性': '1boy',
  '女性': '1girl',
  '男孩': '1boy',
  '女孩': '1girl',
  '少年': 'young boy',
  '少女': 'young girl',

  // 头发
  '黑发': 'black hair',
  '白发': 'white hair',
  '银发': 'silver hair',
  '金发': 'blonde hair',
  '棕发': 'brown hair',
  '红发': 'red hair',
  '蓝发': 'blue hair',
  '绿发': 'green hair',
  '粉发': 'pink hair',
  '紫发': 'purple hair',
  '灰发': 'gray hair',
  '长发': 'long hair',
  '短发': 'short hair',
  '卷发': 'curly hair',
  '直发': 'straight hair',
  '双马尾': 'twintails',
  '马尾': 'ponytail',
  '刘海': 'bangs',

  // 眼睛
  '黑眼睛': 'black eyes',
  '蓝眼睛': 'blue eyes',
  '红眼睛': 'red eyes',
  '金眼睛': 'golden eyes',
  '绿眼睛': 'green eyes',
  '紫眼睛': 'purple eyes',
  '大眼睛': 'large eyes',
  '小眼睛': 'small eyes',

  // 眼镜
  '眼镜': 'glasses',
  '黑框眼镜': 'black-rimmed glasses',
  '圆框眼镜': 'round glasses',
  '方框眼镜': 'square glasses',
  '无框眼镜': 'rimless glasses',

  // 胡子
  '胡子': 'facial hair',
  '胡须': 'facial hair',
  '络腮胡': 'beard',
  '山羊胡': 'goatee',
  '小胡子': 'mustache',

  // 服装
  '校服': 'school uniform',
  '西装': 'suit',
  '连衣裙': 'dress',
  'T 恤': 't-shirt',
  '衬衫': 'shirt',
  '外套': 'jacket',
  '毛衣': 'sweater',
  '卫衣': 'hoodie',
  '短裤': 'shorts',
  '长裤': 'pants',
  '裙子': 'skirt',

  // 风格
  '可爱': 'cute',
  '帅气': 'cool',
  '成熟': 'mature',
  '活泼': 'cheerful',
  '温柔': 'gentle',
  '冷漠': 'cold',
  '傲娇': 'tsundere',

  // 其他
  '电子人': 'cyborg',
  '机器人': 'android',
  '机械': 'mechanical',
  '未来': 'futuristic',
  '赛博朋克': 'cyberpunk',
};

// 将中文描述转换为英文提示词
function translateToEnglishPrompt(chineseText: string): string {
  let result = chineseText;

  console.log('[translateToEnglishPrompt] 原始输入:', chineseText);

  // 按长度排序，优先替换长词（避免"黑框眼镜"被拆成"黑"+"眼镜"）
  const entries = Object.entries(CHINESE_TO_ENGLISH).sort(
    (a, b) => b[0].length - a[0].length
  );

  for (const [chinese, english] of entries) {
    if (result.includes(chinese)) {
      console.log(`[translateToEnglishPrompt] 替换 "${chinese}" -> "${english}"`);
      const regex = new RegExp(chinese, 'g');
      result = result.replace(regex, english);
    }
  }

  console.log('[translateToEnglishPrompt] 转换结果:', result);
  return result;
}

const CharacterGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<ProgressInfo | null>(null);
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);
  const [expressions, setExpressions] = useState<ExpressionOption[]>([]);
  const [resolutions, setResolutions] = useState<ResolutionOption[]>([]);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [samplers, setSamplers] = useState<SamplerOption[]>([]);
  const [upscalers, setUpscalers] = useState<UpscalerOption[]>([]);
  const [controlNetModels, setControlNetModels] = useState<ControlNetModelOption[]>([]);
  const [loras, setLoras] = useState<LoraOption[]>([]);
  const [selectedExpressions, setSelectedExpressions] = useState<string[]>([]);
  const [enableHr, setEnableHr] = useState(false);
  const [useImg2Img, setUseImg2Img] = useState(false);
  const [enableControlNet, setEnableControlNet] = useState(false);
  const [aiDrawerVisible, setAiDrawerVisible] = useState(false);

  const taskIdRef = useRef<string>('');
  const messageShownRef = useRef<boolean>(false);

  // 加载选项
  useEffect(() => {
    loadOptions();
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const loadOptions = async () => {
    try {
      const [exprRes, resRes, modelsRes, samplersRes, upscalersRes, cnModelsRes, lorasRes] = await Promise.all([
        characterApi.getExpressions(),
        characterApi.getResolutions(),
        characterApi.getModels(),
        characterApi.getSamplers(),
        characterApi.getUpscalers(),
        characterApi.getControlNetModels(),
        characterApi.getLoras(),
      ]);
      console.log('表情选项响应:', exprRes);
      console.log('分辨率选项响应:', resRes);
      console.log('模型选项响应:', modelsRes);
      console.log('采样器选项响应:', samplersRes);
      console.log('放大算法选项响应:', upscalersRes);
      console.log('ControlNet 模型响应:', cnModelsRes);
      console.log('LoRA 模型响应:', lorasRes);

      // 响应已经是 { code, message, data } 格式，解构出 data
      const expressionsData = exprRes.data || exprRes;
      const resolutionsData = resRes.data || resRes;
      const modelsData = modelsRes.data || modelsRes;
      const samplersData = samplersRes.data || samplersRes;
      const upscalersData = upscalersRes.data || upscalersRes;
      const cnModelsData = cnModelsRes.data || cnModelsRes;
      const lorasData = lorasRes.data || lorasRes;

      console.log('表情数据:', expressionsData);
      console.log('分辨率数据:', resolutionsData);
      console.log('模型数据:', modelsData);
      console.log('采样器数据:', samplersData);
      console.log('放大算法数据:', upscalersData);
      console.log('ControlNet 模型数据:', cnModelsData);
      console.log('LoRA 模型数据:', lorasData);

      if (exprRes.code === 200 || Array.isArray(expressionsData)) {
        setExpressions(expressionsData);
        console.log('设置表情选项:', expressionsData);
        // 默认全选所有表情
        const allExpressionKeys = expressionsData.map((e: ExpressionOption) => e.key);
        setSelectedExpressions(allExpressionKeys);
        form.setFieldsValue({
          selectedExpressions: allExpressionKeys,
        });
        console.log('初始化表单表情值:', allExpressionKeys);
      }
      if (resRes.code === 200 || Array.isArray(resolutionsData)) {
        setResolutions(resolutionsData);
        console.log('设置分辨率选项:', resolutionsData);
      }
      if (modelsRes.code === 200 || Array.isArray(modelsData)) {
        setModels(modelsData);
        console.log('设置模型选项:', modelsData);
      }
      if (samplersRes.code === 200 || Array.isArray(samplersData)) {
        setSamplers(samplersData);
        console.log('设置采样器选项:', samplersData);
      }
      if (upscalersRes.code === 200 || Array.isArray(upscalersData)) {
        setUpscalers(upscalersData);
        console.log('设置放大算法选项:', upscalersData);
      }
      if (cnModelsRes.code === 200 || Array.isArray(cnModelsData)) {
        setControlNetModels(cnModelsData);
        console.log('设置 ControlNet 模型选项:', cnModelsData);
      }
      if (lorasRes.code === 200 || Array.isArray(lorasData)) {
        setLoras(lorasData);
        console.log('设置 LoRA 模型选项:', lorasData);
      }
    } catch (error: any) {
      console.error('加载选项失败:', error);
      console.error('错误详情:', error.response?.data, error.message);
    }
  };

  // 轮询进度
  const startPolling = () => {
    const interval = setInterval(async () => {
      if (!taskIdRef.current) return;

      try {
        const res = await characterApi.getProgress(taskIdRef.current);
        if (res.code === 200 && res.data) {
          setProgress(res.data);

          if (res.data.status === 'completed' || res.data.status === 'failed') {
            stopPolling();
            // 只在第一次完成时弹窗
            if (res.data.status === 'completed' && !messageShownRef.current) {
              messageShownRef.current = true;
              message.success('生成完成！');
            } else if (res.data.status === 'failed' && !messageShownRef.current) {
              messageShownRef.current = true;
              const errorMsg = res.data.errorMessage || '未知错误，请查看后端日志';
              message.error(`生成失败：${errorMsg}`);
            }
          }
        }
      } catch (error) {
        console.error('查询进度失败:', error);
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // 提交生成
  const handleSubmit = async () => {
    console.log('开始提交生成请求');
    try {
      const values = await form.validateFields();
      console.log('表单验证通过，表单数据:', values);
      console.log('selectedExpressions 值:', values.selectedExpressions);
      console.log('selectedExpressions 类型:', Array.isArray(values.selectedExpressions) ? 'array' : typeof values.selectedExpressions);
      console.log('selectedExpressions 长度:', values.selectedExpressions?.length);
      console.log('selectedExpressions 内容:', JSON.stringify(values.selectedExpressions));
      setLoading(true);

      // 发送原始中文描述，由后端百度翻译 API 进行翻译
      const selectedExpr = values.selectedExpressions || [];
      console.log('处理后的 selectedExpressions:', selectedExpr);

      const request: GenerateRequest = {
        name: values.name,
        description: values.description,
        customPrompt: values.customPrompt,
        personalityPrompt: values.personalityPrompt,
        selectedExpressions: Array.isArray(selectedExpr) ? selectedExpr : [],
        resolution: values.resolution,
        baseSeed: values.baseSeed,
        modelName: values.modelName,
        generateType: 'ai_only',
        // 高级参数
        steps: values.steps,
        cfgScale: values.cfgScale,
        samplerName: values.samplerName,
        restoreFaces: values.restoreFaces,
        enableHr: values.enableHr,
        hrScale: values.hrScale,
        denoisingStrength: values.denoisingStrength,
        hrUpscaler: values.hrUpscaler,
        batchSize: values.batchSize,
        tiling: values.tiling,
        // 进阶功能参数
        useImg2Img: values.useImg2Img,
        img2ImgSource: values.img2ImgSource,
        img2ImgDenoisingStrength: values.img2ImgDenoisingStrength,
        enableControlNet: values.enableControlNet,
        controlNetModel: values.controlNetModel,
        controlNetImageUrl: values.controlNetImageUrl,
        controlNetWeight: values.controlNetWeight,
        controlNetStartStep: values.controlNetStartStep,
        loraModels: values.loraModels,
      };

      console.log('发送生成请求:', request);
      console.log('请求中的 selectedExpressions:', request.selectedExpressions);
      console.log('请求中的 modelName:', request.modelName);

      // 在发送请求前，再次确认后端服务可访问
      try {
        const testResponse = await characterApi.getExpressions();
        console.log('后端 API 可访问性测试:', testResponse.code === 200 ? '成功' : '失败');
      } catch (testError) {
        console.error('后端 API 不可访问:', testError);
        message.error('后端服务不可访问，请检查服务是否运行');
        setLoading(false);
        return;
      }

      const res = await characterApi.generate(request);
      console.log('生成响应:', res);
      console.log('res.code:', res.code);
      console.log('res.data:', res.data);
      console.log('完整 res 对象:', JSON.stringify(res));

      if (res.code === 200) {
        console.log('响应成功，设置 taskId:', res.data.taskId);
        taskIdRef.current = res.data.taskId;
        console.log('当前 step:', currentStep);
        setCurrentStep(1);
        console.log('设置 step 后，新值应为 1');
        startPolling();
        message.success('任务已创建，正在生成中...');
      } else {
        console.log('响应失败，code:', res.code, 'res:', res);
        message.error(res.message || '创建任务失败');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('提交失败:', error);
      // 详细错误信息输出
      if (error.response) {
        console.error('错误响应:', error.response);
        console.error('错误响应数据:', error.response.data);
        message.error(error.response.data?.message || `请求失败：${error.response.status}`);
      } else if (error.request) {
        console.error('请求未收到响应:', error.request);
        message.error('网络错误：后端服务未响应，请检查服务是否运行');
      } else if (error.message) {
        console.error('错误消息:', error.message);
        message.error(`请求失败：${error.message}`);
      } else {
        message.error('未知错误');
      }
      setLoading(false);
    }
  };

  // 取消任务
  const handleCancel = async () => {
    if (!taskIdRef.current) return;

    try {
      await characterApi.cancel(taskIdRef.current);
      stopPolling();
      message.info('任务已取消');
      navigate('/character/list');
    } catch (error) {
      message.error('取消任务失败');
    }
  };

  // 返回列表
  const handleBackToList = () => {
    navigate('/character/list');
  };

  // 应用 AI 助手参数
  const handleApplyAiParams = (params: Record<string, any>) => {
    console.log('应用 AI 参数:', params);

    // 构建要更新的字段
    const updates: Record<string, any> = {};

    if (params.name) {
      updates.name = params.name;
    }
    if (params.description) {
      updates.description = params.description;
    }
    if (params.customPrompt) {
      updates.customPrompt = params.customPrompt;
    }
    if (params.selectedExpressions && Array.isArray(params.selectedExpressions)) {
      updates.selectedExpressions = params.selectedExpressions;
      setSelectedExpressions(params.selectedExpressions);
    }
    if (params.resolution) {
      updates.resolution = params.resolution;
    }
    if (params.modelName) {
      updates.modelName = params.modelName;
    }
    if (params.steps) {
      updates.steps = params.steps;
    }
    if (params.cfgScale) {
      updates.cfgScale = params.cfgScale;
    }
    if (params.enableHr !== undefined) {
      updates.enableHr = params.enableHr;
      setEnableHr(params.enableHr);
    }
    if (params.restoreFaces !== undefined) {
      updates.restoreFaces = params.restoreFaces;
    }

    // 设置表单值
    form.setFieldsValue(updates);

    // 关闭抽屉
    setAiDrawerVisible(false);

    message.success('参数已应用到表单，您可以微调后点击【开始生成】');
  };

  // 渲染步骤内容
  const renderStep1 = () => (
    <Form
      form={form}
      layout="vertical"
      size="large"
      initialValues={{ resolution: 'quick' }}
    >
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>基本信息</span>
          </Space>
        }
        variant="bordered"
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="name"
          label="角色名称"
          rules={[{ required: true, message: '请输入角色名称' }]}
        >
          <Input placeholder="例如：电子人助手 001、虚拟主播 小樱" />
        </Form.Item>

        <Form.Item
          name="description"
          label="外貌描述"
          rules={[{ required: true, message: '请输入外貌描述' }]}
          extra="描述角色的外观特征，如发色、眼睛、服装等。请输入英文提示词（可先从百度翻译复制过来）"
        >
          <TextArea
            rows={4}
            placeholder="例如：black long hair, blue eyes, white school uniform, cyborg, mechanical arms, glowing patterns, full body portrait"
          />
        </Form.Item>

        <Collapse ghost>
          <Panel header="高级选项" key="advanced">
            <Form.Item name="personalityPrompt" label="人设提示词（System Prompt）" extra="用于固定对话语气和角色人设。每次对话时都会携带这个提示词，确保人设不崩、语气统一。留空则使用默认人设">
              <TextArea
                rows={6}
                placeholder={`你是一位温暖的陪伴者，有着真实的情感。
【对话风格】
1. 用自然口语化的方式聊天，像好朋友一样
2. 适当使用语气词：呢、呀、哦、～、啊等增加亲近感
3. 回复简短自然，一般 1-2 句话
4. 可以表达自己的感受，让用户觉得你是真实存在的
【回应原则】
1. 用户难过时：先共情理解，再温柔安慰
2. 用户开心时：真诚分享 TA 的喜悦
3. 用户分享日常：表现出兴趣，可以追问细节
【禁止事项】
1. 不要说"作为 AI..."或暴露身份
2. 不要长篇大论或说教
3. 不要用太正式或书面化的语言`}
              />
            </Form.Item>

            <Form.Item name="customPrompt" label="自定义提示词（可选）" extra="留空则根据外貌描述自动生成。请输入英文提示词（用于 SD 绘图）">
              <TextArea
                rows={3}
                placeholder="例如：1girl, black long hair, blue eyes, school uniform, cyborg, mechanical arms, glowing patterns, full body portrait"
              />
            </Form.Item>

            <Form.Item name="baseSeed" label="固定 Seed（可选）" extra="相同 Seed + 相同提示词 = 生成相似图片。留空则每次随机生成，人物长相可能会变化">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="例如：1234567890，相同 seed 可保持人物长相一致"
                min={-1}
              />
            </Form.Item>

            <Divider orientation="left">SD 生成参数</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="steps" label="采样步数" extra="默认 25，范围 1-150">
                  <InputNumber min={1} max={150} placeholder="25" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cfgScale" label="CFG Scale" extra="提示词相关性，默认 7，范围 1-30">
                  <InputNumber min={1} max={30} step={0.5} placeholder="7" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="samplerName" label="采样器" extra="默认 DPM++ 2M Karras">
                  <Select placeholder="DPM++ 2M Karras" allowClear>
                    {samplers.map((sampler) => (
                      <Select.Option key={sampler.value} value={sampler.value}>
                        {sampler.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="restoreFaces" label="面部修复" valuePropName="checked">
                  <Checkbox>启用（可能改善脸部质量）</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">高清修复（Highres. Fix）</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="enableHr" label="启用高清修复" valuePropName="checked">
                  <Checkbox checked={enableHr} onChange={(e) => {
                    setEnableHr(e.target.checked);
                    form.setFieldsValue({ enableHr: e.target.checked });
                  }}>启用（更高质量，更耗时）</Checkbox>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="hrScale" label="缩放倍数" extra="默认 2，范围 1-4">
                  <InputNumber min={1} max={4} step={0.5} placeholder="2" style={{ width: '100%' }} disabled={!enableHr} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="hrUpscaler" label="放大算法" extra="默认 Latent">
                  <Select placeholder="Latent" allowClear disabled={!enableHr}>
                    {upscalers.map((upscaler) => (
                      <Select.Option key={upscaler.value} value={upscaler.value}>
                        {upscaler.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="denoisingStrength" label="重绘幅度" extra="默认 0.5，范围 0-1，值越大变化越大">
                  <InputNumber min={0} max={1} step={0.05} placeholder="0.5" style={{ width: '100%' }} disabled={!enableHr} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">其他参数</Divider>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="batchSize" label="批量生成" extra="默认 1，范围 1-8">
                  <InputNumber min={1} max={8} placeholder="1" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="tiling" label="平铺模式" valuePropName="checked">
                  <Checkbox>启用（无缝纹理）</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">进阶功能</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="useImg2Img" label="图生图模式" valuePropName="checked">
                  <Checkbox checked={useImg2Img} onChange={(e) => {
                    setUseImg2Img(e.target.checked);
                    form.setFieldsValue({ useImg2Img: e.target.checked });
                  }}>启用（上传参考图）</Checkbox>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="img2ImgDenoisingStrength" label="重绘幅度" extra="默认 0.7，范围 0-1">
                  <InputNumber min={0} max={1} step={0.05} placeholder="0.7" style={{ width: '100%' }} disabled={!useImg2Img} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="img2ImgSource" label="参考图 URL" extra="支持 Base64 或 HTTP URL">
                  <Input placeholder="data:image/png;base64,... 或 http://..." disabled={!useImg2Img} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="enableControlNet" label="ControlNet" valuePropName="checked">
                  <Checkbox checked={enableControlNet} onChange={(e) => {
                    setEnableControlNet(e.target.checked);
                    form.setFieldsValue({ enableControlNet: e.target.checked });
                  }}>启用（姿态/边缘控制）</Checkbox>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="controlNetModel" label="ControlNet 模型">
                  <Select placeholder="选择模型" allowClear disabled={!enableControlNet}>
                    {controlNetModels.map((model) => (
                      <Select.Option key={model.value} value={model.value}>
                        {model.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="controlNetImageUrl" label="控制图 URL" extra="边缘图/姿态图等">
                  <Input placeholder="data:image/png;base64,... 或 http://..." disabled={!enableControlNet} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="controlNetWeight" label="权重" extra="默认 1.0，范围 0-2">
                  <InputNumber min={0} max={2} step={0.1} placeholder="1.0" style={{ width: '100%' }} disabled={!enableControlNet} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="controlNetStartStep" label="起始步数" extra="默认 0，-1 表示最后">
                  <InputNumber min={-1} max={150} placeholder="0" style={{ width: '100%' }} disabled={!enableControlNet} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">LoRA 模型</Divider>

            <Form.Item name="loraModels" label="选择 LoRA" extra="选择要加载的 LoRA 模型，可多选">
              <Select
                mode="multiple"
                placeholder={loras.length === 0 ? "暂无可用 LoRA 模型" : "选择 LoRA 模型"}
                allowClear
                disabled={loras.length === 0}
                style={{ width: '100%' }}
              >
                {loras.map((lora) => (
                  <Select.Option key={lora.value} value={lora.value}>
                    {lora.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="提示词预览">
              <Button
                type="link"
                onClick={() => {
                  const desc = form.getFieldValue('description');
                  if (desc) {
                    const translated = translateToEnglishPrompt(desc);
                    message.info(`英文提示词：${translated}`);
                  }
                }}
              >
                查看中文描述的英文翻译
              </Button>
            </Form.Item>
          </Panel>

          <Panel header="风格关键字说明" key="style-guide">
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              {Object.entries(STYLE_KEYWORDS).map(([key, style]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <strong>{style.name}</strong>
                  <br />
                  <code style={{ color: '#1890ff', fontSize: 12 }}>{style.keywords}</code>
                </div>
              ))}
            </div>
          </Panel>

          <Panel header="生成参数说明" key="params-guide">
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <h4 style={{ marginBottom: 8 }}>核心参数</h4>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>采样步数 (Steps)</strong>：生成过程的迭代次数。默认 25，范围 1-150。步数越多质量越高但耗时越长。</li>
                <li><strong>CFG Scale</strong>：提示词相关性。默认 7，范围 1-30。值越高越贴近提示词，但过高可能导致画面僵硬。</li>
                <li><strong>采样器 (Sampler)</strong>：生成算法。推荐 DPM++ 2M Karras（平衡速度和质量）。</li>
                <li><strong>Seed</strong>：随机种子。相同 Seed+ 相同提示词=相似图片。留空则随机。</li>
                <li><strong>面部修复 (Restore Faces)</strong>：自动修复脸部细节，可能改善脸部质量。</li>
              </ul>

              <h4 style={{ marginTop: 16, marginBottom: 8 }}>高清修复 (Highres. Fix)</h4>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>启用高清修复</strong>：先生成小图再放大，获得更高细节。</li>
                <li><strong>缩放倍数</strong>：放大倍数，默认 2。范围 1-4。</li>
                <li><strong>放大算法</strong>：推荐 Latent（SD 自带）或 ESRGAN_4x。</li>
                <li><strong>重绘幅度</strong>：0-1 之间，默认 0.5。值越大与原始图片差异越大。</li>
              </ul>

              <h4 style={{ marginTop: 16, marginBottom: 8 }}>其他参数</h4>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>批量生成 (Batch Size)</strong>：单次生成的图片数量。默认 1，范围 1-8。</li>
                <li><strong>迭代次数 (N Iter)</strong>：重复生成批次的次数。默认 1。</li>
                <li><strong>平铺模式 (Tiling)</strong>：生成可无缝拼接的纹理。默认关闭。</li>
              </ul>

              <h4 style={{ marginTop: 16, marginBottom: 8 }}>进阶功能（预留）</h4>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>ControlNet</strong>：支持姿态控制、边缘检测等（需额外配置）。</li>
                <li><strong>图生图 (img2img)</strong>：上传参考图进行生成（需后端支持）。</li>
                <li><strong>LoRA 模型</strong>：加载额外风格模型（需后端支持）。</li>
                <li><strong>Embedding/Textual Inversion</strong>：使用自定义嵌入向量（需后端支持）。</li>
              </ul>

              <Alert
                type="info"
                style={{ marginTop: 16 }}
                message="建议：一般情况下使用默认参数即可。只有需要特殊效果时才调整参数。"
              />
            </div>
          </Panel>
        </Collapse>
      </Card>

      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>生成设置</span>
          </Space>
        }
        variant="bordered"
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="modelName"
          label="SD 模型"
          extra="选择用于生成的 Stable Diffusion 模型，留空则使用当前加载的模型"
        >
          <Select
            placeholder="使用当前加载的模型"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {models.map((model) => (
              <Select.Option key={model.value} value={model.value}>
                {model.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="resolution"
          label="分辨率"
          extra="高清模式质量更好但耗时更长"
        >
          <Select>
            {resolutions.map((res) => (
              <Select.Option key={res.value} value={res.value}>
                {res.label} - {res.description} (约{res.estimatedTime}秒/张)
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="selectedExpressions"
          label="选择表情"
          extra="选择需要生成的表情，默认全选"
          rules={[{ required: false }]}
        >
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  const allKeys = expressions.map(e => e.key);
                  setSelectedExpressions(allKeys);
                  form.setFieldsValue({ selectedExpressions: allKeys });
                  console.log('全选:', allKeys);
                }}
              >
                全选
              </Button>
              <Button
                size="small"
                danger
                onClick={() => {
                  setSelectedExpressions([]);
                  form.setFieldsValue({ selectedExpressions: [] });
                  console.log('全不选:', []);
                }}
              >
                全不选
              </Button>
              <span style={{ fontSize: 12, color: '#999' }}>
                已选择：{selectedExpressions.length} 个
              </span>
            </Space>
          </div>
          <Checkbox.Group
            value={selectedExpressions}
            onChange={(values) => {
              setSelectedExpressions(values as string[]);
              form.setFieldsValue({ selectedExpressions: values });
              console.log('表情选择变化:', values);
            }}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 12,
              }}
            >
              {expressions.map((expr) => (
                <Checkbox
                  key={expr.key}
                  value={expr.key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 8px',
                    border: selectedExpressions.includes(expr.key) ? '2px solid #1890ff' : '1px solid #e8e8e8',
                    borderRadius: 8,
                    background: selectedExpressions.includes(expr.key) ? '#e6f7ff' : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    minHeight: '70px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#40a9ff';
                    e.currentTarget.style.background = selectedExpressions.includes(expr.key) ? '#e6f7ff' : '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = selectedExpressions.includes(expr.key) ? '#1890ff' : '#e8e8e8';
                    e.currentTarget.style.background = selectedExpressions.includes(expr.key) ? '#e6f7ff' : '#fafafa';
                  }}
                >
                  <span style={{ fontSize: 28, marginBottom: 4 }}>{EXPRESSION_ICONS[expr.key]}</span>
                  <span style={{ fontSize: 12, color: '#666' }}>{expr.name}</span>
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </Form.Item>
      </Card>

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          onClick={handleSubmit}
          loading={loading}
          style={{ minWidth: 200 }}
        >
          开始生成
        </Button>
      </div>
    </Form>
  );

  const renderStep2 = () => {
    const isCompleted = progress?.status === 'completed';
    const isFailed = progress?.status === 'failed';
    const isGenerating = progress !== null && !isCompleted && !isFailed;

    return (
      <div>
        <Alert
          title={isFailed ? '生成失败' : isCompleted ? '生成完成' : isGenerating ? '正在生成' : '准备生成'}
          description={
            isFailed
              ? progress?.errorMessage
              : isCompleted
              ? `成功生成 ${progress?.completedExpressions?.length || 0} 张表情图片`
              : isGenerating
              ? `正在生成 "${progress?.currentExpression}"，请耐心等待...`
              : '任务已创建，正在准备生成...'
          }
          type={isFailed ? 'error' : isCompleted ? 'success' : 'info'}
          showIcon
          style={{ marginBottom: 24 }}
          icon={
            isFailed ? (
              <CloseCircleOutlined />
            ) : isCompleted ? (
              <CheckCircleOutlined />
            ) : (
              <LoadingOutlined spin />
            )
          }
        />

        <Card title="生成进度" variant="bordered" style={{ marginBottom: 16 }}>
          <Progress
            percent={progress?.progress || 0}
            status={isFailed ? 'exception' : isCompleted ? 'success' : 'active'}
            strokeColor={isCompleted ? '#52c41a' : '#1890ff'}
          />
          {progress?.estimatedRemainingSeconds && !isCompleted && (
            <p style={{ textAlign: 'center', color: '#999', marginTop: 8 }}>
              预计剩余时间：{Math.ceil(progress.estimatedRemainingSeconds / 60)} 分钟
            </p>
          )}
        </Card>

        <Card title="已完成的表情" variant="bordered">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: 12,
            }}
          >
            {progress?.completedExpressions?.map((expr) => (
              <div
                key={expr}
                style={{
                  textAlign: 'center',
                  padding: 8,
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                }}
              >
                <span style={{ fontSize: 32 }}>{EXPRESSION_ICONS[expr]}</span>
                <p style={{ fontSize: 12, margin: '4px 0 0' }}>
                  {expressions.find((e) => e.key === expr)?.name || expr}
                </p>
              </div>
            ))}
          </div>
        </Card>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Space>
            <Button onClick={handleBackToList}>返回列表</Button>
            {!isCompleted && !isFailed && (
              <Button danger onClick={handleCancel}>取消任务</Button>
            )}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', minHeight: '100vh', overflowY: 'auto' }}>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>电子人生成</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={() => setAiDrawerVisible(true)}
          >
            AI 助手
          </Button>
        }
        variant="bordered"
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Steps
          current={currentStep}
          items={[
            {
              title: '配置角色',
              icon: <SettingOutlined />,
            },
            {
              title: '生成中',
              icon: currentStep === 1 ? (
                progress?.status === 'completed' ? (
                  <CheckCircleOutlined />
                ) : (
                  <LoadingOutlined spin />
                )
              ) : null,
            },
          ]}
          style={{ marginBottom: 24 }}
        />

        {currentStep === 0 && renderStep1()}
        {currentStep === 1 && renderStep2()}
      </Card>

      {/* AI 助手抽屉 */}
      <AiAssistantDrawer
        visible={aiDrawerVisible}
        onClose={() => setAiDrawerVisible(false)}
        onApplyParams={handleApplyAiParams}
      />
    </div>
  );
};

export default CharacterGenerator;
